import { Button } from "@volca/ui/components/ui/button";
import {
  RETURN_AIR_GRILLE,
  SUMP_PUMP,
  THERMOSTAT,
} from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { count, listOf } from "@volca/ui/trades";
import type { EmptyActions } from "@/profitability/template-page/use-empty-actions";
import type { OverviewChecks } from "./types";

/** What Edit agent is handed to add a check, for the owner to finish. */
const CHECK_DRAFT = "Add a check to this agent: ";

/**
 * Why a period has nothing to review, from its checks (`overview.checks`):
 * the agent checks nothing (or every check is off), a check could not run on
 * every record so nothing flagged is not all clear, a check on other records
 * (technicians) flagged some, or every check ran and passed (or, for checks
 * that only report what they flag, found nothing).
 * For a period that flagged nothing; one that did has a list to review.
 */
export const ChecksResult = ({
  checks,
  noun,
  plural,
  period,
  actions,
  onAsk,
}: {
  checks: OverviewChecks;
  /** What the checks look at: "job", "jobs". */
  noun: string;
  plural: string;
  /** "week", "month". */
  period: string;
  actions: EmptyActions;
  /** Asks Volca about the period, in the chat beside the page. */
  onAsk: (question: string) => void;
}) => {
  const on = checks.rules.filter((rule) => rule.state !== "disabled");
  const off = checks.rules.length - on.length;
  if (on.length === 0)
    return off > 0 ? (
      <MachineEmptyState
        art={RETURN_AIR_GRILLE}
        title="Every check is switched off"
        description={`Nothing is flagged while this agent's ${count(off, "check")} ${off === 1 ? "is" : "are"} off.${actions.limits ? " Turn them back on in Settings." : ""}`}
      >
        {actions.limits && (
          <Button variant="secondary" onClick={actions.limits}>
            Open settings
          </Button>
        )}
      </MachineEmptyState>
    ) : (
      <MachineEmptyState
        art={RETURN_AIR_GRILLE}
        title={`This agent doesn't check ${plural} yet`}
        description={
          actions.change
            ? `It reports the numbers but holds nothing to a rule, so nothing is flagged for review. Add a check for what you'd want to hear about${plural === "jobs" ? ", like jobs where labor runs over 45% of revenue" : ""}.`
            : "It reports the numbers but holds nothing to a rule, so nothing is flagged for review. Someone who manages this agent can add checks."
        }
      >
        {actions.change && (
          <Button onClick={() => actions.change?.(CHECK_DRAFT)}>
            Add a check
          </Button>
        )}
      </MachineEmptyState>
    );
  // What is said of the Review's records (jobs) comes from the checks on
  // them; a check on another dataset (technicians) is said in its own terms.
  const work = on.filter((rule) => rule.work);
  const unrun = work.filter(
    (rule) => rule.state === "not_evaluated" || rule.state === "partial",
  );
  if (unrun.length > 0) {
    const names = listOf(
      unrun.map((rule) => rule.label),
      3,
    );
    return (
      <MachineEmptyState
        art={THERMOSTAT}
        title={
          unrun.length === 1
            ? `A check couldn't look at every ${noun}`
            : `${unrun.length} checks couldn't look at every ${noun}`
        }
        description={`${names} couldn't check every ${noun} this ${period}, so nothing flagged doesn't mean all clear. A figure ${unrun.length === 1 ? "it needs" : "they need"} was probably missing from the files.`}
      >
        <Button
          variant="secondary"
          onClick={() =>
            onAsk(
              `Why couldn't ${names} check every ${noun} this ${period}, and what is missing?`,
            )
          }
        >
          Ask Volca why
        </Button>
      </MachineEmptyState>
    );
  }
  // Nothing among the Review's records, while a check on other records did
  // flag some: say which, never that the checks found nothing.
  const elsewhere = on.filter((rule) => !rule.work && rule.flagged > 0);
  if (elsewhere.length > 0) {
    // In a sentence, as the page says its own records ("3 technicians").
    const named = (rule: OverviewChecks["rules"][number]) => ({
      noun: (rule.noun ?? "record").toLowerCase(),
      plural: (rule.plural ?? "records").toLowerCase(),
    });
    const caught = elsewhere.map((rule) => {
      const { noun: one, plural: many } = named(rule);
      return `${rule.label} flagged ${count(rule.flagged, one, many)}`;
    });
    const first = elsewhere[0];
    return (
      <MachineEmptyState
        art={THERMOSTAT}
        title={`No ${plural} flagged this ${period}`}
        description={`${listOf(caught, caught.length)}.${work.length > 0 ? ` ${work.length === 1 ? "The check" : `The ${work.length} checks`} on ${plural} found nothing.` : ""}`}
      >
        <Button
          variant="secondary"
          onClick={() =>
            onAsk(
              `Which ${named(first).plural} did ${first.label} flag this ${period}, and why?`,
            )
          }
        >
          Ask Volca about them
        </Button>
      </MachineEmptyState>
    );
  }
  // Every record checked by every check is all clear; a check that only
  // reports what it flags found nothing, which is all that can be said.
  const labels = listOf(
    on.map((rule) => rule.label),
    on.length,
  );
  const switchedOff =
    off > 0
      ? ` ${count(off, "check")} ${off === 1 ? "is" : "are"} switched off.`
      : "";
  const limits = actions.limits && (
    <Button variant="secondary" onClick={actions.limits}>
      Adjust limits
    </Button>
  );
  const records = checks.records;
  // All clear counts the Review's records, so only when every check is on them.
  if (records && on.every((rule) => rule.state === "evaluated" && rule.work))
    return (
      <MachineEmptyState
        art={SUMP_PUMP}
        title={`All clear this ${period}`}
        description={`All ${count(records, noun, plural)} passed ${on.length === 1 ? "the check" : `all ${on.length} checks`}: ${labels}.${switchedOff}`}
      >
        {limits}
      </MachineEmptyState>
    );
  return (
    <MachineEmptyState
      art={SUMP_PUMP}
      title={`Nothing flagged this ${period}`}
      description={`${on.length === 1 ? "Its check" : `Its ${on.length} checks`} found nothing: ${labels}.${switchedOff}`}
    >
      {limits}
    </MachineEmptyState>
  );
};
